import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import type { AiLandingPageSuggestion } from "@mirage/analytics/events/enums/ai_landing_page_suggestion";
import {
  logSelectDropboxAiAskEvent,
  logShownDropboxAiEvent,
} from "@mirage/mosaics/Chat/components/chat/instructions/bridge_growth/analytics";
import {
  getPromptEnums,
  useEmptyStateSuggestions,
} from "@mirage/mosaics/Chat/components/chat/instructions/bridge_growth/hooks/useEmptyStateSuggestions";
import { useFSSChatContext } from "@mirage/mosaics/Chat/components/chat/instructions/bridge_growth/hooks/useFSSChatContext";
import { PromotionSuggestionChip } from "@mirage/mosaics/Chat/components/chat/instructions/bridge_growth/PromotionSuggestionChip";
import { PromotionWriteWithTemplateChip } from "@mirage/mosaics/Chat/components/chat/instructions/bridge_growth/PromotionWriteWithTemplateChip";
import type {
  PAPPromptEnum,
  Suggestion,
} from "@mirage/mosaics/Chat/components/chat/instructions/bridge_growth/types";
import { SuggestionType } from "@mirage/mosaics/Chat/components/chat/instructions/bridge_growth/types";
import {
  getFileExtension,
  parseDocumentInfos,
  parseEntityId,
  parseFolderPath,
  parseScenarioMeta,
} from "@mirage/mosaics/Chat/components/chat/instructions/bridge_growth/utils";
import { useComposeCurrentSessionContext } from "@mirage/mosaics/ComposeAssistant/data/current-session/ComposeCurrentSessionContext";
import { useChatRoute } from "@mirage/mosaics/ConversationAssistant/hooks/useChatRoute";
import { useQuerySuggestions } from "@mirage/mosaics/QuerySuggestions/useQuerySuggestions";
import type { FssAskFromSuggestionParams } from "@mirage/service-conversation/router_state_types";
import { CtaSource } from "@mirage/service-conversation/router_state_types";
import type {
  AgentId,
  ConversationClientContext,
} from "@mirage/service-conversation/types";
import { buildConversationRouterState } from "@mirage/service-conversation/utils/buildConversationRouterState";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import { useDashActionSurface } from "@mirage/shared/hooks/useDashActionSurface";
import React, { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../EmptyChatInstructions.module.css";

const SKELETON_LOADING_SUGGESTIONS = 3;

// Helper to check if enum is a write template (FSS_WRITE suggestion)
const isWriteTemplateEnum = (enumValue: string): boolean =>
  ["marketing_blog_post", "sales_outreach_email", "status_report"].includes(
    enumValue,
  );

export interface PromotionQuerySuggestionContainerProps {
  clientContext?: ConversationClientContext;
  isFirstTimeChat?: boolean;
  handlePostQuerySubmit: (hideFollowUpSuggestions: boolean) => void;
}

export const PromotionQuerySuggestionContainer: React.FC<
  PromotionQuerySuggestionContainerProps
> = ({ isFirstTimeChat, handlePostQuerySubmit }) => {
  const navigate = useNavigate();
  const chatRoute = useChatRoute();
  const { source, files, folders, surface } = useFSSChatContext();
  const { reportPapEvent } = useMirageAnalyticsContext();
  const { currentSessionID } = useComposeCurrentSessionContext();
  const { dashActionSurface } = useDashActionSurface();

  const isFolderScopedChatEnabled = useFeatureFlagValueBool(
    "bridge_fss_chat_2025_07_28_folder_scoped_chat",
  );

  // Move useEmptyStateSuggestions higher up so it's available for analytics
  const { suggestions, loading: writeTemplateLoading } =
    useEmptyStateSuggestions({
      source,
      isFirstTimeChat: !!isFirstTimeChat,
      extension: getFileExtension(files[0]?.name),
      filesCount: files.length,
    });

  const { isLoading: isLoadingSuggestions, querySuggestions } =
    useQuerySuggestions({
      shouldFetchMaxSuggestions: true,
      // TODO: (DISCOVERY-7541) Fix bug with extra call to genQuerySuggestions
      maxSuggestions: 3,
      demoSuggestions: [],
      options: {
        source: "dash",
        retriever: "fss",
        fssSearchScope: parseFolderPath(folders),
        documentIds: [], // parseEntityId([...files]),
        documentInfos: parseDocumentInfos([...files]),
        //TODO(DISCOVERY-7403): use stacks when implemented
      },
    });

  // Create analytics callback to avoid prop drilling
  const logWithPromptEnums = useCallback(
    (suggestionEnum: PAPPromptEnum) => {
      // Only wait for write template loading if this is an FSS_WRITE suggestion
      const shouldWaitForWriteTemplateLoading =
        isWriteTemplateEnum(suggestionEnum as string) && writeTemplateLoading;

      if (
        shouldWaitForWriteTemplateLoading ||
        !suggestions ||
        !querySuggestions ||
        !source ||
        !currentSessionID ||
        suggestionEnum === undefined ||
        isFirstTimeChat === undefined
      ) {
        return; // Don't log during loading or if data is incomplete
      }

      const promptEnums = getPromptEnums(suggestions, querySuggestions);
      logSelectDropboxAiAskEvent(reportPapEvent, {
        source,
        chatSessionId: currentSessionID,
        prompts: promptEnums,
        selectedPromptText: suggestionEnum as AiLandingPageSuggestion,
        isFirstTimeChat: !!isFirstTimeChat,
        dashActionSurface,
      });
    },
    [
      writeTemplateLoading,
      suggestions,
      querySuggestions,
      source,
      currentSessionID,
      isFirstTimeChat,
      reportPapEvent,
      dashActionSurface,
    ],
  );

  const handleClick = ({
    query,
    agentIDs = [],
    attachments = [],
    hideFollowupSuggestions = false,
    selectedPromptEnum,
  }: {
    query: string;
    agentIDs?: AgentId[];
    attachments?: string[];
    hideFollowupSuggestions?: boolean;
    selectedPromptEnum?: PAPPromptEnum;
  }) => {
    // Log select.dropbox_ai_ask event
    if (selectedPromptEnum) {
      logWithPromptEnums(selectedPromptEnum);
    }

    const askConversationParams: FssAskFromSuggestionParams = {
      message: query,
      attachments,
      agentIds: agentIDs,
      scenarioMeta: parseScenarioMeta(source, files, folders),
      surface,
    };
    const routerState = buildConversationRouterState(
      CtaSource.FssOneClickAskPrompt,
      askConversationParams,
    );
    navigate(chatRoute, { state: routerState });
    handlePostQuerySubmit(hideFollowupSuggestions);
  };

  const getLoadingSuggestionChip = useCallback((key: React.Key) => {
    return (
      <PromotionSuggestionChip
        key={key}
        text={"Loading..."}
        isLoading={true}
        onSubmit={() => {}}
      />
    );
  }, []);

  let dynamicIndex = 0;
  const renderSuggestion = useCallback(
    (suggestion: Suggestion, index: number) => {
      const key = `${source}-${index}`;

      switch (suggestion.type) {
        case SuggestionType.STATIC:
          return (
            <PromotionSuggestionChip
              key={key}
              text={suggestion.getSuggestionText()}
              onSubmit={() => {
                handleClick({
                  query: suggestion.getSuggestionText(),
                  hideFollowupSuggestions: suggestion.hasPrescribedAnswer,
                  selectedPromptEnum: suggestion.enum,
                });
              }}
            />
          );

        case SuggestionType.CONTEXTUAL:
          if (source === "fss-folder" && !isFolderScopedChatEnabled) {
            // Hide contextual suggestion chip(s) for folder scoped chat until improvement
            return null;
          }

          return (
            <PromotionSuggestionChip
              key={key}
              text={suggestion.getSuggestionText()}
              onSubmit={() => {
                handleClick({
                  query: suggestion.getSuggestionText(),
                  agentIDs: suggestion
                    .getSuggestionText()
                    .toLowerCase()
                    .includes("summarize")
                    ? ["summarize"]
                    : [],
                  //TODO(DISCOVERY-7403): use stacks when implemented
                  attachments: parseEntityId([...files, ...folders]),
                  selectedPromptEnum: suggestion.enum,
                });
              }}
            />
          );

        case SuggestionType.DYNAMIC: {
          if (querySuggestions.length <= dynamicIndex) {
            return null;
          }
          const dynamicSuggestion = querySuggestions[dynamicIndex++];
          return dynamicSuggestion ? (
            <PromotionSuggestionChip
              key={key}
              {...dynamicSuggestion}
              isLoading={isLoadingSuggestions}
              onSubmit={() => {
                handleClick({
                  query: dynamicSuggestion.text,
                  attachments: dynamicSuggestion.docIds,
                  selectedPromptEnum: suggestion.enum,
                  agentIDs: dynamicSuggestion?.agentIds || [],
                });
              }}
            />
          ) : null;
        }
        case SuggestionType.FSS_WRITE:
          return (
            <PromotionWriteWithTemplateChip
              key={key}
              suggestionEnum={suggestion.enum}
              onSubmit={logWithPromptEnums}
              loading={writeTemplateLoading}
            />
          );

        default:
          return null;
      }
    },
    [
      source,
      files,
      folders,
      handleClick,
      querySuggestions,
      isLoadingSuggestions,
      isFolderScopedChatEnabled,
    ],
  );

  const getSkeletonLoadingChips = () => {
    // Pass an array-like object to map over and return loading query chips
    return Array.from({ length: SKELETON_LOADING_SUGGESTIONS }).map(
      (_, index) => getLoadingSuggestionChip(`loading-chip-${index}`),
    );
  };

  // Show loading skeleton while initializing source context or fetching suggestions.
  const isLoading = source === undefined || isLoadingSuggestions;

  // Log shown.dropbox_ai event when API completes and we have suggestions
  const hasLoggedForSession = useRef(false);

  useEffect(() => {
    // Reset flag when loading starts (new session)
    if (isLoadingSuggestions) {
      hasLoggedForSession.current = false;
      return;
    }

    // Only log once per session when loading completes
    if (!hasLoggedForSession.current && suggestions.length > 0) {
      const promptEnums = getPromptEnums(suggestions, querySuggestions);

      // Only wait for write template loading if prompt enums contain FSS_WRITE templates
      const shouldWaitForWriteTemplateLoading =
        promptEnums.some((enumValue) =>
          isWriteTemplateEnum(enumValue as string),
        ) && writeTemplateLoading;

      if (shouldWaitForWriteTemplateLoading) {
        hasLoggedForSession.current = false;
        return;
      }

      hasLoggedForSession.current = true;
      logShownDropboxAiEvent(reportPapEvent, {
        source,
        chatSessionId: currentSessionID,
        prompts: promptEnums,
        isFirstTimeChat: !!isFirstTimeChat,
        dashActionSurface,
      });
    }
  }, [isLoadingSuggestions, writeTemplateLoading, source, currentSessionID]);

  return (
    <div className={styles.SuggestionTiles} key={source}>
      {isLoading
        ? getSkeletonLoadingChips()
        : suggestions
            .map((suggestion, index) => renderSuggestion(suggestion, index))
            .filter(Boolean)}
    </div>
  );
};

PromotionQuerySuggestionContainer.displayName =
  "PromotionQuerySuggestionContainer";
